import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  TablePagination,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { useTranslation } from "react-i18next";
import CardGroup from "../../../Components/Common/CardGroup";

import Card1 from "../../../assets/Cards/Card1.webp";
import Card2 from "../../../assets/Cards/Card2.webp";
import Card3 from "../../../assets/Cards/Card3.webp";
import Card4 from "../../../assets/Cards/Card4.webp";

import { DropDownIcon } from "../../../Components/Layouts/CustomIcons";
import LocationPopup from "../../../Pages/Views/LocationPopup";
import TransactionsTable from "../../Views/Transcations/TransactionsTable";
import {
  getReturnCards,
  getReturnTrack,
  getReturnTransactionsData,
} from "../../../Services/authService";
import dayjs from "dayjs";
import { formatDate } from "../../../Components/Common/dateFormat";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../../../Styles/daterange-responsive.css";
import { DateRange } from "react-date-range";
import * as XLSX from "xlsx";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import "./Returns.css";

function Returns() {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const containerBackground =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const btnColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const btnColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale10;
  const borderColor1 =
    mode === "dark" ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale20;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const settingIconColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;
  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;
  const pillBackground = mode === "dark" ? DARKCOLORS.GreyScale10 : "#FFFFFF";
  const pillBorder =
    mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E8E8F0";
  const pillTextColor = mode === "dark" ? DARKCOLORS.GreyScale80 : "#444444";
  const iconColor = mode === "dark" ? DARKCOLORS.GreyScale60 : "#888888";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [machineLocations, setMachineLocations] = useState([]);
  const [selectedLocationNames, setSelectedLocationNames] = useState([]);
  const [returnsRows, setReturnsRows] = useState([]);

  const [summary, setSummary] = useState({
    customerPendingCount: 0,
    guestReturnedCount: 0,
    guestPendingCount: 0,
    customerReturnedCount: 0,
  });

  const [totalItems, setTotalItems] = useState(0);
  const [noDataAlert, setNoDataAlert] = useState({ open: false, message: "" });

  const btnColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  // Date range state
  const [dateRange, setDateRange] = useState([
    {
      startDate: dayjs().startOf("month").toDate(),
      endDate: dayjs().toDate(),
      key: "selection",
    },
  ]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const calendarRef = useRef(null);
  const calendarButtonRef = useRef(null);

  // null = no time filter (show ALL returns); set only when the user applies a range
  const [appliedDateRange, setAppliedDateRange] = useState(null);
  // Min/max date of the fetched data — shown on the calendar button when no filter is applied
  const [dataDateRange, setDataDateRange] = useState(null);

  const handleCalendarToggle = () => {
    if (calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
      });
    }
    setIsCalendarOpen((prev) => !prev);
  };

  const handleDateChange = (item) => {
    setDateRange([item.selection]);
  };

  const handleApply = () => {
    setIsCalendarOpen(false);
    setAppliedDateRange({ ...dateRange[0] });
    setPage(0);
  };

  const handleReset = () => {
    setAppliedDateRange(null);
    setDateRange([{ key: "selection" }]);
    setPage(0);
    setIsCalendarOpen(false);
  };

  const handleRowSelect = (row) => {
    if (selectedRows.some((x) => x.id === row.id)) {
      setSelectedRows(selectedRows.filter((x) => x.id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(returnsRows);
    }
    setSelectAll(!selectAll);
  };

  const handleLocationClick = () => {
    setIsPopupOpen(true);
  };

  const handleLocationPopupClose = () => {
    setIsPopupOpen(false);
  };

  const handleLocationSubmit = (locations) => {
    const selectedIds = [];
    const selectedNames = [];

    locations.forEach((location) => {
      (location.machines || []).forEach((machine) => {
        if (machine.machineId) {
          selectedIds.push(machine.machineId);
        }

        // Use machine location for Returns API
        const machineLocation = (machine.location || "").trim();

        if (machineLocation && !selectedNames.includes(machineLocation)) {
          selectedNames.push(machineLocation);
        }
      });
    });

    setMachineLocations(selectedIds);
    setSelectedLocationNames(selectedNames);
    setPage(0);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const cardData = [
    {
      value: summary.customerIssuedCount,
      label: t("returns.cards.customer_issued"),
      background: Card1,
    },

    {
      value: summary.customerReturnedCount,
      label: t("returns.cards.customer_returned"),
      background: Card3,
    },
    {
      value: summary.guestIssuedCount,
      label: t("returns.cards.guest_issued"),
      background: Card2,
    },

    {
      value: summary.guestReturnedCount,
      label: t("returns.cards.guest_returned"),
      background: Card4,
    },
  ];

  const normalizeStatus = (status) => {
    const map = {
      RETURNED_ON_TIME: "returned_on_time",
      RETURNED_LATE: "returned_late",
      RETURNED: "returned",
      NOT_RETURNED: "not_returned",
      PARTIAL_RETURNED: "partial_returned",
    };
    return map[status] || status;
  };

  const fetchReturnsData = async () => {
    try {
      const summaryParams = {
        machineType: "Return Vending Machine",
      };
      // Only narrow by date once the user actively applies a range;
      // by default no time filter is sent so ALL returns are shown
      if (appliedDateRange) {
        summaryParams.fromDate = dayjs(appliedDateRange.startDate)
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");
        summaryParams.toDate = dayjs(appliedDateRange.endDate)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");
      }
      if (selectedLocationNames.length > 0) {
        summaryParams.location = selectedLocationNames.join(",");
      }
      const summaryResponse = await getReturnCards(summaryParams);
      setSummary(summaryResponse);

      const dataParams = {
        machineType: "Return Vending Machine",
        page,
        size: rowsPerPage,
      };

      if (statusFilter) {
        dataParams.status = statusFilter;
      }

      if (selectedLocationNames.length > 0) {
        dataParams.location = selectedLocationNames.join(",");
      }

      const dataResponse = await getReturnTrack(dataParams);

      if (dataResponse.items?.length > 0) {
      }

      let formattedRows = (dataResponse.items || []).map((item) => ({
        id: item.id,

        customer: item.customerName || item.customer,

        location: item.location,

        issuedBowls: item.issuedBowls ?? 0,

        returnedBowls: item.returnedBowls ?? 0,

        pendingBowls: item.pendingBowls ?? 0,

        returnedAt: item.returnedAt,

        dueDate: item.dueDate || item.deadLine,

        status: normalizeStatus(item.status),
      }));

      // Filter by the visible Due Date column (client-side, end-of-day inclusive)
      if (appliedDateRange) {
        const start = new Date(appliedDateRange.startDate).getTime();
        const end = new Date(appliedDateRange.endDate).setHours(
          23,
          59,
          59,
          999,
        );
        formattedRows = formattedRows.filter((row) => {
          if (!row.dueDate) return true;
          const due = new Date(row.dueDate).getTime();
          return due >= start && due <= end;
        });
      }

      setReturnsRows(formattedRows);
      setTotalItems(dataResponse.totalItems);

      // Reflect the actual data range in the calendar when no filter is applied
      if (!appliedDateRange) {
        const timestamps = (dataResponse.items || [])
          .map((item) => {
            const d = item.dueDate || item.deadLine;
            return d ? new Date(d).getTime() : null;
          })
          .filter(Boolean);
        if (timestamps.length > 0) {
          const range = {
            startDate: new Date(Math.min(...timestamps)),
            endDate: new Date(Math.max(...timestamps)),
          };
          setDataDateRange(range);
          if (!isCalendarOpen) {
            setDateRange([{ ...range, key: "selection" }]);
          }
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchReturnsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    machineLocations,
    selectedLocationNames,
    page,
    rowsPerPage,
    appliedDateRange,
  ]);

  // Close the calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const Green100 = DARKCOLORS.Success100;
  const Green700 = DARKCOLORS.Success700;
  const Warning100 = DARKCOLORS.Warning100;
  const LightBackground = LIGHTCOLORS.BodyLightBackground;
  const Alert100 = DARKCOLORS.Danger100;

  const statusStyles = {
    returned_on_time: {
      bg: Green100,
      color: Green700,
      label: t("returns.status.returned_on_time"),
    },
    returned_late: {
      bg: Warning100,
      color: LightBackground,
      label: t("returns.status.returned_late"),
    },
    returned: {
      bg: Green100,
      color: Green700,
      label: t("returns.status.returned"),
    },
    not_returned: {
      bg: Alert100,
      color: Green700,
      label: t("returns.status.not_returned"),
    },
    partial_returned: {
      bg: "#f2d773 ",
      color: "#000000",
      label: t("returns.status.partial_returned"),
    },
  };

  // Direct export of ALL rows matching the current filters (location, status,
  // applied date range) regardless of the visible page
  const handleExport = async () => {
    if (!totalItems) {
      setNoDataAlert({ open: true, message: t("discounts.Nodataexport") });
      return;
    }
    try {
      const params = {
        machineType: "Return Vending Machine",
        page: 0,
        size: totalItems,
      };
      // Date filtering is client-side here too (see fetchReturnsData)
      if (statusFilter) params.status = statusFilter;
      //       if (selectedLocationNames.length > 0) {
      //   params.location = selectedLocationNames.join(",");
      // }

      const dataResponse = await getReturnTrack(params);
      let items = dataResponse.items || [];
      if (appliedDateRange) {
        const start = new Date(appliedDateRange.startDate).getTime();
        const end = new Date(appliedDateRange.endDate).setHours(
          23,
          59,
          59,
          999,
        );
        items = items.filter((item) => {
          const due = item.dueDate || item.deadLine;
          if (!due) return true;
          const t = new Date(due).getTime();
          return t >= start && t <= end;
        });
      }

      if (items.length === 0) {
        setNoDataAlert({ open: true, message: t("discounts.Nodataexport") });
        return;
      }

      // Translated Excel headers, mirroring the table columns
      const headerMapping = {
        customer: t("returns.table.customer"),
        bowls: t("returns.table.bowls"),
        location: t("returns.table.location"),
        returnedOn: t("returns.table.returned_on"),
        dueDate: t("returns.table.due_date"),
        cashback: t("returns.table.cashback"),
        creditedTo: t("returns.table.credited_to"),
        // points: t("returns.table.points"),
        status: t("returns.table.status"),
      };

      const transformedData = items.map((item) => {
        const statusKey = normalizeStatus(item.status);
        return {
          [headerMapping.customer]: item.customerName || item.customer || "-",
          [headerMapping.bowls]: item.bowls ?? item.quantity ?? 0,
          [headerMapping.location]: item.location || "-",
          [headerMapping.returnedOn]: formatDate(
            item.returnedAt || item.returnedOn || item.returnedDate,
          ),
          [headerMapping.dueDate]: formatDate(item.dueDate || item.deadLine),
          [headerMapping.cashback]: `€ ${item.cashback ?? 0}`,
          [headerMapping.creditedTo]:
            item.creditedTo === "NAF Wallet"
              ? t("transactions.nafWallet")
              : item.creditedTo || "-",
          // [headerMapping.points]: item.points ?? 0,
          [headerMapping.status]:
            statusStyles[statusKey]?.label || item.status || "-",
        };
      });

      // Generate timestamp
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14);

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Returns");

      // Write file with timestamped name
      const fileName = t("returns.export_file_name", "Returns");

      XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
    } catch (error) {
      console.error("Returns Export Error:", error);
    }
  };

  const columns = [
    {
      field: "customer",
      label: t("returns.table.customer"),
    },

    {
      field: "location",
      label: t("returns.table.location"),
      align: "center",
    },

    {
      field: "issuedBowls",
      label: t("returns.table.issued"),

      renderCell: (row) => row.issuedBowls ?? 0,
    },

    {
      field: "returnedBowls",
      label: t("returns.table.returned"),

      renderCell: (row) => row.returnedBowls ?? 0,
    },

    {
      field: "pendingBowls",
      label: t("returns.table.pending"),

      renderCell: (row) => row.pendingBowls ?? 0,
    },

    {
      field: "returnedOn",
      label: t("returns.table.returned_on"),
      align: "center",
      renderCell: (row) => formatDate(row.returnedAt),
    },

    {
      field: "dueDate",
      label: t("returns.table.due_date"),
      align: "center",
      renderCell: (row) => formatDate(row.dueDate),
    },

    {
      field: "status",
      label: t("returns.table.status"),
      renderCell: (row) => {
        const s = row.status?.toLowerCase();

        const cfg = statusStyles[s] || {
          bg: "#F3F4F6",
          color: "#374151",
          label: s || "-",
        };

        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "4px 12px",
              borderRadius: "99px",
              background: cfg.bg,
              color: cfg.color,
              width: "fit-content",
              fontSize: "13px",
            }}
          >
            {cfg.label}
          </Box>
        );
      },
    },
  ];

  return (
    <div
      className={`dashboard-container-overview ${mode === "dark" ? "dark-mode" : ""}`}
    >
      {/* Page Heading */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <div
          className="page-heading"
          style={{ color: settingIconColor, fontSize: "24px", cursor: "auto" }}
        >
          {t("returns.tabs.returns")}
        </div>
      </Box>

      {/* Cards */}
      <CardGroup cardData={cardData} />

      {/* Page Heading */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <div
          className="page-heading"
          style={{ color: settingIconColor, fontSize: "24px", cursor: "auto" }}
        >
          {t("returns.tabs.bowl_tracking")}
        </div>
      </Box>

      {/* Filters + Table Section */}
      <Box
        className="return-filter-container"
        sx={{
          background: containerBackground,
          borderRadius: "12px",
          mt: 3,
          pb: "24px",
        }}
      >
        <Box
          className="return-filter-box"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "7px",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Button
            onClick={handleLocationClick}
            sx={{
              borderRadius: "24px",
              padding: "9px 20px",
              color: settingIconColor,
              background: btnColor,
              textTransform: "none",
              minWidth: "auto",
              width: "fit-content",
            }}
            endIcon={<DropDownIcon color1={dropCircle} color2={dropIcon} />}
          >
            {machineLocations.length > 0
              ? t("returns.select_location")
              : t("returns.all_locations")}
          </Button>
          <Button
            className="return-date-mobile"
            ref={calendarButtonRef}
            aria-label="select-date"
            onClick={handleCalendarToggle}
            sx={{
              background: btnColor,
              borderRadius: "50px",
              padding: "8px 14px",
              color: dropIcon,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: settingIconColor,
              }}
            >
              {appliedDateRange?.startDate && appliedDateRange?.endDate
                ? `${dayjs(appliedDateRange.startDate).format("DD MMM YYYY")} - ${dayjs(appliedDateRange.endDate).format("DD MMM YYYY")}`
                : t("sales_analytics.select_date_range", "Select Date Range")}
            </Typography>
            <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
          </Button>

          <LocationPopup
            open={isPopupOpen}
            onClose={handleLocationPopupClose}
            selectedMachineLocations={machineLocations}
            onSubmit={handleLocationSubmit}
            machineType="Return Vending Machine"
          />

          <Box
            className="return-filter-box"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* Date Range Toggle */}
            <Button
              className="return-date-website"
              ref={calendarButtonRef}
              aria-label="select-date"
              onClick={handleCalendarToggle}
              sx={{
                background: btnColor,
                borderRadius: "50px",
                padding: "8px 14px",
                color: dropIcon,
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: settingIconColor,
                }}
              >
                {appliedDateRange?.startDate && appliedDateRange?.endDate
                  ? `${dayjs(appliedDateRange.startDate).format("DD MMM YYYY")} - ${dayjs(appliedDateRange.endDate).format("DD MMM YYYY")}`
                  : t("sales_analytics.select_date_range", "Select Date Range")}
              </Typography>
              <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
            </Button>

            {/* Date Range Picker */}
            {isCalendarOpen && (
              <Box
                className="sales-calender"
                ref={calendarRef}
                sx={{
                  background: btnColor,
                  position: "absolute",
                  top: `${calendarPosition.top}px`,
                  right: "25px",
                  zIndex: 1000,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "12px",
                  border: `1px solid ${borderColor2}`,
                }}
              >
                <DateRange
                  editableDateInputs={true}
                  onChange={handleDateChange}
                  moveRangeOnFirstSelection={false}
                  ranges={dateRange}
                  style={{
                    borderRadius: "12px",
                    padding: "8px",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.5rem",
                    mt: 1,
                    p: "8px",
                  }}
                >
                  <button
                    type="button"
                    className="cancel-btn"
                    style={{
                      color: btnColor1,
                      borderColor: btnColor1,
                      padding: "10px 15px",
                      fontSize: "15px",
                    }}
                    onClick={handleReset}
                  >
                    {t("sales_analytics.reset", "Reset")}
                  </button>
                  <button
                    type="button"
                    style={{
                      background: btnColor1,
                      padding: "10px 15px",
                      fontSize: "15px",
                    }}
                    className="save-btn"
                    onClick={handleApply}
                  >
                    {t("sales_analytics.apply")}
                  </button>
                </Box>
              </Box>
            )}

            <Select
              className="fil"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              displayEmpty
              size="small"
              IconComponent={(props) => (
                <DropDownIcon
                  color1={dropCircle}
                  color2={dropIcon}
                  {...props}
                  sx={{
                    fontSize: "20px",
                    marginRight: "50px",
                    "@media (max-width: 426px)": {
                      fontSize: "15px",
                    },
                  }}
                />
              )}
              sx={{
                borderRadius: "24px",
                border: `0px solid ${borderColor1}`,
                textAlign: "center",
                paddingRight: "13px",
                "& .MuiSelect-select": {
                  padding: "9px 20px 9px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "@media (max-width: 426px)": {
                    padding: "8px 30px 8px 15px !important",
                    fontSize: "13px !important",
                  },
                  "@media (max-width: 376px)": {
                    padding: "6px 25px 5px 10px !important",
                    fontSize: "13px !important",
                  },
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: `0.5px solid ${borderColor1}`,
                },
                "@media (max-width: 425px) and (min-width:300px)": {
                  padding: "2px 8px",
                  fontSize: "8px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    border: `0px solid ${borderColor1}`,
                    boxShadow: "none",
                    backgroundColor: containerBackground,
                    padding: "10px",
                    marginTop: "10px",
                  },
                },
              }}
              renderValue={(selected) => {
                if (selected === "") {
                  return (
                    <p className="sort-filter-text">
                      {t("membershipCards.filter")}
                    </p>
                  );
                }
                const filterLabels = {
                  returned_on_time: "returns.status.returned_on_time",
                  returned_late: "returns.status.returned_late",
                  returned: "returns.status.returned",
                  not_returned: "returns.status.not_returned",
                  partial_returned: "returns.status.partial_returned",
                };
                return t(filterLabels[selected]) || selected;
              }}
            >
              <MenuItem className="menu-item-sec" value="">
                {t("membershipCards.all")}
              </MenuItem>
              <MenuItem className="menu-item-sec" value="returned_on_time">
                {t("returns.status.returned_on_time")}
              </MenuItem>
              <MenuItem className="menu-item-sec" value="returned_late">
                {t("returns.status.returned_late")}
              </MenuItem>
              <MenuItem className="menu-item-sec" value="returned">
                {t("returns.status.returned")}
              </MenuItem>
              <MenuItem className="menu-item-sec" value="not_returned">
                {t("returns.status.not_returned")}
              </MenuItem>
              <MenuItem className="menu-item-sec" value="partial_returned">
                {t("returns.status.partial_returned")}
              </MenuItem>
            </Select>

            {/* Export Btn */}
            <Button
              id="export-btn"
              style={{
                fontSize: "13px",
                color: settingIconColor,
                background: "transparent",
                border: `0.5px solid ${borderColor1}`,
                padding: "9.5px 16px",
                borderRadius: "20px",
              }}
              className="export-sales-btn  margin-zero"
              onClick={handleExport}
              endIcon={<FileUploadOutlinedIcon />}
            >
              {t("returns.export")}
            </Button>
          </Box>
        </Box>

        <TransactionsTable
          rows={returnsRows}
          columns={columns}
          selectedRows={selectedRows}
          selectAll={selectAll}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          // maxHeight="calc(110vh - 310px)"
        />

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage={t("membershipCards.RowsPerPage", "Rows Per Page")}
          sx={{
            display: "flex",
            justifyContent: "center",
            overflowX: "auto",
            // Same custom scrollbar as the table container
            "&::-webkit-scrollbar": {
              width: "10px !important",
              height: "10px !important",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: scroll1,
              borderRadius: "10px",
              border: `1px solid ${scroll}`,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scroll,
              borderRadius: "10px",
              border: "3px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                backgroundColor: scroll,
              },
            },
          }}
        />
      </Box>

      {/* No Data Export Alert */}
      <SuccessNotification
        open={noDataAlert.open}
        message={noDataAlert.message}
        severity="warning"
        onClose={() => setNoDataAlert({ open: false, message: "" })}
      />
    </div>
  );
}

export default Returns;
